import React, { useState, useEffect, useMemo } from 'react';
import syncService from '../../services/syncService';
import Spinner from './Spinner';
import SyncProjectPicker from './SyncProjectPicker';
import './ConfirmModal.css';
import './CopyConversionsModal.css';

// Copy conversions — or DEFAULT VALUES — from another sync project. A two-step wizard.
//
// ONE COMPONENT FOR BOTH, because they are one thing in the database. Conversions and default values
// share `syncprojectdataconversion` and are told apart by a single property: a conversion HAS a source
// value (map this to that), a default value does NOT (supply this where the record has none). The
// screen behind this modal already splits them that way; `isDefault` carries the same split in here, so
// a Defaults screen can never show or copy a conversion, and vice versa. Forking this into a second
// modal would mean two copies of the diff, the selection and the safety rules.
//
// WHY IT IS NOT ONE BUTTON ANY MORE. It used to take a project and merge EVERY conversion it had into
// this one, in a single click, with no way to see beforehand what that would bring across. Even though
// the merge itself skipped duplicates, the user could not tell what they were about to inherit — which
// made a routine "borrow that property's mappings" into something nobody wanted to press.
//
// So: pick a project, then see the DIFFERENCE — only the conversions the other project has that this
// one does not — and tick the ones you want. Rows this project already holds are never shown, never
// sent, and cannot be overwritten (the server inserts with INSERT IGNORE, see insertConversionIfMissing).
//
// THE DIFF IS COMPUTED IN THE BROWSER. Both sides are already available as ordinary conversion lists —
// this project's from the screen behind the modal, the other's from the same GET the screen itself
// uses — so a dedicated compare endpoint would be a second implementation of the same subtraction, free
// to disagree with the one the user is looking at.

// The identity of a conversion: what makes two rows "the same mapping". Deliberately EXCLUDES the
// target value — a row that maps the same source to a DIFFERENT target is not missing here, it is a
// disagreement, and silently adding it would either be ignored by the server or (worse) look like it
// changed this project's own mapping. Those are surfaced separately as conflicts.
const keyOf = (c) => [
    String(c.recordType || '').trim().toUpperCase(),
    String(c.sourceFieldName == null ? '' : c.sourceFieldName).trim().toUpperCase(),
    String(c.sourceValue || '').trim().toUpperCase(),
].join(' ');

// A row with no source value is a DEFAULT VALUE; a row with one is a CONVERSION. Same table, same
// endpoints — this is the only thing that separates them, on this screen and on the one behind it.
const isDefaultRow = (c) => String(c.sourceValue == null ? '' : c.sourceValue).trim() === '';

const CopyConversionsModal = ({
    accountId, currentSyncUuid, templateName,
    currentConversions = [],       // what THIS project already has — the left side of the diff
    isDefault = false,             // Default Values task rather than Conversions
    onConfirm, onClose,
}) => {
    // Wording, so the screen never calls a default value a conversion.
    const noun    = isDefault ? 'default value' : 'conversion';
    const nouns   = isDefault ? 'default values' : 'conversions';
    const plural  = (n, one = noun, many = nouns) => (n === 1 ? one : many);
    const [step, setStep]                     = useState('project');   // 'project' | 'review'
    const [selected, setSelected]             = useState(null);
    const [sourceConversions, setSourceConversions] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [loadError, setLoadError]           = useState(null);
    const [checked, setChecked]               = useState(() => new Set());
    const [reviewFilter, setReviewFilter]     = useState('');
    const [confirming, setConfirming]         = useState(false);
    // Escape backs out one step rather than closing outright — losing a reviewed selection to a stray
    // keypress is exactly the kind of thing this rewrite exists to stop.
    useEffect(() => {
        const onKey = e => {
            if (e.key !== 'Escape') return;
            if (step === 'review') { setStep('project'); return; }
            onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose, step]);

    // BOTH SIDES filtered to the same kind of row before anything is compared. Without this a Defaults
    // screen would diff its default values against the other project's conversions, and offer to copy
    // rows it cannot even display.
    const mineByKey = useMemo(() => {
        const m = new Map();
        (currentConversions || [])
            .filter(c => isDefaultRow(c) === isDefault)
            .forEach(c => m.set(keyOf(c), c));
        return m;
    }, [currentConversions, isDefault]);

    // The three buckets. `missing` is what can be copied; `conflicts` is shown read-only so the user
    // understands why a row they expected is absent; `same` is only ever a count.
    const diff = useMemo(() => {
        if (!Array.isArray(sourceConversions)) return null;
        const missing = [], conflicts = [];
        let same = 0;
        for (const c of sourceConversions.filter(r => isDefaultRow(r) === isDefault)) {
            const mine = mineByKey.get(keyOf(c));
            if (!mine) { missing.push(c); continue; }
            const a = String(mine.targetValue == null ? '' : mine.targetValue).trim();
            const b = String(c.targetValue == null ? '' : c.targetValue).trim();
            if (a === b) same++; else conflicts.push({ ...c, mineTargetValue: mine.targetValue });
        }
        return { missing, conflicts, same };
    }, [sourceConversions, mineByKey, isDefault]);

    const loadSource = async (project) => {
        setLoadingPreview(true); setLoadError(null); setSourceConversions(null); setSelected(project);
        try {
            const details = await syncService.getSyncProjectDetails(project.syncuuid);
            // EITHER task will do. getConversions resolves the rows from the syncId alone — the task
            // uuid is only in the URL — and conversions and default values live in one store, so a
            // project that has only a Default Values task can still be read from a Conversions screen
            // and the other way round. Insisting on the matching task name would refuse projects whose
            // rows are perfectly readable.
            const convTask = (details.projectTasks || [])
                .find(t => t.taskName === 'CONVERSIONS' || t.taskName === 'DEFAULTVALUES');
            if (!convTask) { setLoadError('This project has no conversions or default values task.'); return; }
            const rows = await syncService.getConversions(project.syncuuid, convTask.syncProjectTaskUuid);
            setSourceConversions(Array.isArray(rows) ? rows : []);
        } catch {
            setLoadError(`The ${nouns} for this project could not be loaded.`);
        } finally {
            setLoadingPreview(false);
        }
    };

    const handleSelect = (project) => {
        if (selected?.syncuuid === project.syncuuid) {
            setSelected(null); setSourceConversions(null); setLoadError(null); return;
        }
        loadSource(project);
    };

    // Everything missing starts TICKED. The user opened this to copy; making them select all as a first
    // action would be busywork, and the list they are agreeing to is on screen either way.
    const goToReview = () => {
        if (!diff) return;
        setChecked(new Set(diff.missing.map(keyOf)));
        setReviewFilter('');
        setStep('review');
    };

    const visibleMissing = useMemo(() => {
        if (!diff) return [];
        const q = reviewFilter.trim().toLowerCase();
        if (!q) return diff.missing;
        return diff.missing.filter(c =>
            String(c.recordType || '').toLowerCase().includes(q) ||
            String(c.sourceFieldName == null ? '' : c.sourceFieldName).toLowerCase().includes(q) ||
            String(c.sourceValue || '').toLowerCase().includes(q) ||
            String(c.targetValue || '').toLowerCase().includes(q));
    }, [diff, reviewFilter]);

    const toggle = (c) => {
        const k = keyOf(c);
        setChecked(prev => {
            const next = new Set(prev);
            if (next.has(k)) next.delete(k); else next.add(k);
            return next;
        });
    };

    // Select-all acts on WHAT IS VISIBLE, not on the whole list — with a filter applied, a control that
    // silently ticked hidden rows would copy things the user never saw.
    const allVisibleChecked = visibleMissing.length > 0 && visibleMissing.every(c => checked.has(keyOf(c)));
    const toggleAllVisible = () => {
        setChecked(prev => {
            const next = new Set(prev);
            visibleMissing.forEach(c => { if (allVisibleChecked) next.delete(keyOf(c)); else next.add(keyOf(c)); });
            return next;
        });
    };

    const handleConfirm = async () => {
        if (!selected || !diff) return;
        const chosen = diff.missing.filter(c => checked.has(keyOf(c)));
        if (!chosen.length) return;
        setConfirming(true);
        try { await onConfirm(selected, chosen); }
        finally { setConfirming(false); }
    };

    const canReview = !!selected && !loadingPreview && !loadError && !!diff && diff.missing.length > 0;
    const chosenCount = diff ? diff.missing.filter(c => checked.has(keyOf(c))).length : 0;

    // A DEFAULT VALUE HAS NO SOURCE, so the source column and the arrow are dropped entirely rather
    // than rendered empty — a blank column and a dangling arrow read as missing data.
    const Row = ({ c, extra }) => (
        <>
            <span className="ccm-cell ccm-cell--type">{c.recordType}</span>
            <span className="ccm-cell ccm-cell--field">{String(c.sourceFieldName == null ? '' : c.sourceFieldName)}</span>
            {!isDefault && <span className="ccm-cell ccm-cell--from" title={c.sourceValue}>{c.sourceValue}</span>}
            {!isDefault && <span className="ccm-cell ccm-cell--arrow" aria-hidden="true">&rarr;</span>}
            <span className="ccm-cell ccm-cell--to" title={c.targetValue}>{c.targetValue}</span>
            {extra}
        </>
    );

    return (
        <div className="cm-backdrop" onClick={onClose}>
            <div className="cm-dialog ccm-dialog" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">

                {step === 'project' && (
                    <>
                        <h2 className="cm-title">Copy {nouns} &mdash; choose a project</h2>
                        <p className="ccm-hint">
                            Step 1 of 2. Nothing is copied yet &mdash; you will choose which {nouns} to bring across next.
                        </p>

                        <SyncProjectPicker
                            accountId={accountId}
                            currentSyncUuid={currentSyncUuid}
                            templateName={templateName}
                            selected={selected}
                            busy={loadingPreview}
                            onSelect={handleSelect}
                            renderBadge={() => (
                                loadingPreview ? <Spinner size="sm" />
                                : loadError ? <span className="ccm-badge ccm-badge--grey">Could not load</span>
                                : diff ? (
                                    <span className={`ccm-badge ${diff.missing.length > 0 ? 'ccm-badge--green' : 'ccm-badge--grey'}`}>
                                        {diff.missing.length > 0 ? `${diff.missing.length} new` : 'nothing new'}
                                    </span>
                                ) : null
                            )}
                        />

                        {selected && !loadingPreview && (loadError || diff) && (
                            <div className={`ccm-summary ${canReview ? 'ccm-summary--green' : 'ccm-summary--amber'}`}>
                                {loadError ? loadError
                                    : diff.missing.length === 0
                                        ? `"${selected.projectName}" has no ${nouns} this project does not already have`
                                          + (diff.conflicts.length ? `, but ${diff.conflicts.length} ${plural(diff.conflicts.length)} disagree.` : '.')
                                        : `${diff.missing.length} ${plural(diff.missing.length)} to review`
                                          + (diff.same ? ` · ${diff.same} already identical` : '')
                                          + (diff.conflicts.length ? ` · ${diff.conflicts.length} conflicting` : '')}
                            </div>
                        )}

                        <div className="cm-actions">
                            <button className="cm-btn-cancel" onClick={onClose}>Cancel</button>
                            <button className="cm-btn-confirm" onClick={goToReview} disabled={!canReview}>
                                {canReview ? `Review ${diff.missing.length} ${plural(diff.missing.length)}` : 'Review'}
                            </button>
                        </div>
                    </>
                )}

                {step === 'review' && diff && (
                    <>
                        <h2 className="cm-title">Copy {nouns} &mdash; choose what to add</h2>
                        <p className="ccm-hint">
                            Step 2 of 2. Only {nouns} <strong>{selected.projectName}</strong> has that this project
                            does not. Nothing already here is changed or removed.
                        </p>

                        <div className="ccm-review-bar">
                            <label className="ccm-selectall">
                                <input type="checkbox" checked={allVisibleChecked} onChange={toggleAllVisible} />
                                <span>{allVisibleChecked ? 'Deselect' : 'Select'} all{reviewFilter ? ' shown' : ''}</span>
                            </label>
                            <input
                                className="ccm-search ccm-search--inline"
                                type="text"
                                placeholder="Filter..."
                                value={reviewFilter}
                                onChange={e => setReviewFilter(e.target.value)}
                            />
                            <span className="ccm-count">{chosenCount} of {diff.missing.length} selected</span>
                        </div>

                        <div className={`ccm-list ccm-difflist${isDefault ? ' ccm-difflist--nosource' : ''}`}>
                            <div className="ccm-diffhead">
                                <span />
                                <span className="ccm-cell--type">Record type</span>
                                <span className="ccm-cell--field">Field</span>
                                {!isDefault && <span className="ccm-cell--from">Source value</span>}
                                {!isDefault && <span />}
                                <span className="ccm-cell--to">{isDefault ? 'Default value' : 'Target value'}</span>
                            </div>
                            {visibleMissing.length === 0 && (
                                <div className="ccm-empty">No {nouns} match "{reviewFilter}".</div>
                            )}
                            {visibleMissing.map(c => {
                                const k = keyOf(c);
                                return (
                                    <label key={k} className={`ccm-diffrow${checked.has(k) ? ' ccm-diffrow--on' : ''}`}>
                                        <input type="checkbox" checked={checked.has(k)} onChange={() => toggle(c)} />
                                        <Row c={c} />
                                    </label>
                                );
                            })}
                        </div>

                        {/* Shown, never selectable. A row whose source value this project already maps
                            SOMEWHERE ELSE is the one a user is most likely to come looking for, and
                            "it wasn't in the list" is a much worse answer than showing it greyed out
                            with both values side by side. */}
                        {diff.conflicts.length > 0 && (
                            <details className="ccm-conflicts">
                                <summary>
                                    {diff.conflicts.length} {plural(diff.conflicts.length)}
                                    {isDefault
                                        ? ' set the same field to something different here'
                                        : ' map the same source value to something different here'} &mdash; not copied
                                </summary>
                                <div className={`ccm-list ccm-difflist ccm-difflist--conflict${isDefault ? ' ccm-difflist--nosource' : ''}`}>
                                    {diff.conflicts.map(c => (
                                        <div key={keyOf(c)} className="ccm-diffrow ccm-diffrow--conflict">
                                            <span />
                                            <Row c={c} extra={<span className="ccm-cell ccm-cell--mine">here: {c.mineTargetValue}</span>} />
                                        </div>
                                    ))}
                                </div>
                            </details>
                        )}

                        <div className="cm-actions">
                            <button className="cm-btn-cancel" onClick={() => setStep('project')} disabled={confirming}>Back</button>
                            <button className="cm-btn-confirm" onClick={handleConfirm} disabled={!chosenCount || confirming} autoFocus>
                                {confirming ? 'Copying...' : `Copy ${chosenCount} ${plural(chosenCount)}`}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CopyConversionsModal;
